import AbstractPage from './AbstractPage';
import 'fullpage.js/vendors/scrolloverflow';
import Player from '@vimeo/player';
import fullpage from 'fullpage.js';
import { TweenMax, TimelineMax } from 'gsap';
import { resizeVideoIframe } from '../utils/resizeVideoIframe';
import * as debounce from 'debounce';

export default class Homepage extends AbstractPage {
  init() {
    this.fp = new fullpage(this.sliderEl, {
      licenseKey: '8042ADCD-C03F4BFE-88A2E87A-73D15923',
      sectionSelector: '.homepage-section',
      onLeave: this.onLeave.bind(this),
      afterLoad: this.afterLoad.bind(this),
      verticalCentered: false,
      responsiveWidth: 1200,
      responsiveHeight: 620,
      scrollOverflow: true,
      afterRender: () => {
        if (this.$heroVideoEl.length) {
          this.hasVideo = true;
          this.initVideo();
        }
      },
    });

    this.updatePage({
      slideIndex: this.fp.getActiveSection().index,
    });

    this.bindEvents();

    window.WK.utilsBus.$emit('nav:show', true);
  }

  declareProperties() {
    this.hasVideo = false;
    this.sliderEl = this.el.querySelector('.js-homepage-slider');
    this.$heroVideoEl = this.$el.find('[data-video-id]');
  }

  bindEvents() {
    this.$el.find('.js-scroll-down').on('click', (ev) => {
      ev.preventDefault();
      this.fp.moveSectionDown();
    });
  }

  initVideo() {
    const $play = this.$heroVideoEl.find('[data-video-play]');
    const $mute = this.$heroVideoEl.find('[data-video-mute]');

    this.videoId = this.$heroVideoEl.data('video-id');
    this.videoPlayer = new Player(this.$heroVideoEl, {
      id: this.videoId,
      width: 640,
      height: 360,
      loop: true,
      background: true,
      byline: false,
      controls: false,
      title: false,
      portrait: false,
    });

    const resizeIframe = () => {
      return resizeVideoIframe({
        $el: this.$heroVideoEl,
        $iframe: this.$heroVideoEl.find('iframe'),
        player: this.videoPlayer,
      });
    };

    this.videoPlayer.ready().then(() => {
      resizeIframe();
      $play.addClass('is-visible');
      window.addEventListener('resize', debounce(() => resizeIframe(), 200));
    });

    $play.on('click', (e) => {
      e.preventDefault();
      $play.removeClass('is-visible');
      $mute.addClass('is-visible');
      this.videoTriggered = true;
      this.videoPlayer.setVolume(1);
    });

    $mute.on('click', (e) => {
      $play.addClass('is-visible');
      $mute.removeClass('is-visible');
      this.videoPlayer.setVolume(0);
    });
  }

  startVideo() {
    // if (this.hasVideo && this.videoPlayer) {
    //   this.videoPlayer.play();
    // }
  }

  stopVideo() {
    // if (this.hasVideo && this.videoPlayer) {
    //   this.videoPlayer.pause();
    // }
  }

  onLeave(origin, destination) {
    if (origin.index === 0 && this.hasVideo) {
      this.stopVideo();
    }

    this.updatePage({
      slideIndex: destination.index,
      el: destination.item,
    });

    this.animateSectionBeforeLoad({
      slideIndex: destination.index,
      el: destination.item,
    });
  }

  afterLoad(origin, destination) {
    if (destination.index === 0 && this.hasVideo) {
      this.startVideo();
    }

    this.animateSectionAfterLoad({
      slideIndex: destination.index,
      el: destination.item,
    });
  }

  updatePage({ slideIndex }) {
    let logoShow = true;
    let logoColor = 'white';
    let navColor = 'white';
    let controls = 'white';

    switch (slideIndex) {
      case 0:
        logoShow = false;
        logoColor = 'transparent';
        navColor = 'white';
        controls = 'white';
        break;
      case 1:
        logoColor = 'white';
        navColor = 'gold';
        controls = 'gold';
        break;
      case 2:
        logoColor = 'gold';
        navColor = 'white';
        controls = 'white';
        break;
      case 3:
        logoColor = 'white';
        navColor = 'gold';
        controls = 'gold';
        break;
      case 4:
        logoColor = 'gold';
        navColor = 'white';
        controls = 'white';
        break;
      default:
        logoColor = 'gold';
        navColor = 'gold';
        controls = 'gold';
        break;
    }

    window.WK.utilsBus.$emit('nav:color', navColor);
    window.WK.utilsBus.$emit('logo:color', logoColor);
    window.WK.utilsBus.$emit('logo:show', logoShow);
  }

  animateSectionBeforeLoad({ el }) {
    if (this.wasAnimated({ el })) {
      return;
    }

    const { image } = this.getSectionElements({ el });

    if (image) {
      TweenMax.fromTo(image, 2, { scale: 1.2 }, { scale: 1 });
    }
  }

  animateSectionAfterLoad({ slideIndex, el }) {
    switch (slideIndex) {
      case 0:
        this.animateHero({ el });
        break;
      case 1:
        this.animateTheBand({ el });
        break;
      case 2:
        this.animateDj({ el });
        break;
      case 3:
        this.animateAcoustic({ el });
        break;
      case 4:
        this.animateEvent({ el });
        break;
    }
  }

  animateHero({ el }) {
    if (this.wasAnimated({ el })) {
      return;
    }

    const logo = el.querySelector('.homepage-hero__logo');
    const cta = el.querySelector('.homepage-hero__cta');

    const timeline = new TimelineMax();

    if (logo) {
      timeline.fromTo(logo, 2, { opacity: 0 }, { opacity: 1 });
    }

    if (cta) {
      timeline.fromTo(cta, 2, { opacity: 0, y: 50 }, { opacity: 1, y: 0 }, '-=.5');
    }

    this.setAnimated({ el });

    return timeline;
  }

  animateTheBand({ el }) {
    this.animateContentElements({ el, invert: false });
  }

  animateDj({ el }) {
    this.animateContentElements({ el, invert: true });
  }

  animateAcoustic({ el }) {
    this.animateContentElements({ el, invert: false });
  }

  animateEvent({ el }) {
    this.animateContentElements({ el, invert: false });
  }

  resetContentElements({ el }) {
    const { title, subtitle, text, cta } = this.getSectionElements({ el });

    TweenMax.set([title, subtitle, text, cta], { opacity: 0 });
  }

  animateContentElements({ el, invert = false }) {
    if (this.wasAnimated({ el })) {
      return;
    }

    const { title, subtitle, text, cta } = this.getSectionElements({ el });

    const fromX = invert ? 50 : -50;

    const timeline = new TimelineMax();

    if (title) {
      timeline.fromTo(title, 2, { opacity: 0, x: fromX }, { opacity: .9, x: 0 });
    }
    if (subtitle) {
      timeline.fromTo(subtitle, 2, { opacity: 0, x: fromX }, { opacity: .9, x: 0 }, '-=1.5');
    }
    if (text) {
      timeline.fromTo(text, 2, { opacity: 0, x: fromX }, { opacity: 1, x: 0 }, '-=1.5');
    }
    if (cta) {
      timeline.fromTo(cta, 2, { opacity: 0, x: fromX }, { opacity: 1, x: 0 }, '-=1.5');
    }

    this.setAnimated({ el });

    return timeline;
  }

  getSectionElements({ el }) {
    const title = el.querySelector('.homepage-section__title');
    const subtitle = el.querySelector('.homepage-section__subtitle');
    const text = el.querySelector('.homepage-section__text');
    const cta = el.querySelector('.homepage-section__cta');
    const image = el.querySelector('.homepage-section__image');

    return {
      title,
      subtitle,
      text,
      cta,
      image,
    };
  }

  wasAnimated({ el }) {
    return el.dataset.animated === '1';
  }

  setAnimated({ el, animated = true }) {
    el.dataset.animated = animated ? '1' : '0';
  }

}
